import React, { useState } from "react";
import "../Sidebar/Sidebar.scss";
import HeaderLogo from "../../assets/images/logo/header-logo.svg";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import { Link, useNavigate ,useLocation } from "react-router-dom";
import { useAppSelector } from "../../Store/store";
import PersonalizeLearningDialog from "../PersonalizeLearningDialog/PersonalizeLearningDialog";

interface MenuProps {
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

// sidebar tooltip 
const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow placement="right" classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#20232D',
    '&::before': {
      backgroundColor: '#20232D',
    },
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#20232D', 
    boxShadow: '0px 12px 24px 0px rgba(134, 140, 152, 0.12)',
    fontSize: '14px',
    fontWeight: 400,
    height: '30px',
    border: '0px',
    borderRadius: '2px',
    textAlign: 'center',
    padding: '4px 10px',
    fontFamily: '$font_family_body',
  },
}));

const Sidebar: React.FC<MenuProps> = ({ isMenuOpen, onMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // const [showDialog, setShowDialog] = useState(false);
  const user = useAppSelector(state => state.user.user);
  
  // Check if current path is an admin path
  const isAdminPath = location.pathname.startsWith("/admin");

  return (
    <>
      <div className="sidebar-wrapper">
        <div className={`sidebar-menu${isMenuOpen ? " open" : ""}`}>
          <List>
            {isAdminPath ? (
              <>
                <ListItem disablePadding>
                  <BootstrapTooltip title="Dashboard">
                    <ListItemButton 
                      className={location.pathname === "/admin" ? "active" : ""} 
                      onClick={() => { navigate("/admin") }}
                    >
                      <ListItemIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <path
                            d="M9.9 17.0999V8.0999H17.1V17.0999H9.9ZM0.9 9.8999V0.899902H8.1V9.8999H0.9ZM6.3 8.0999V2.6999H2.7V8.0999H6.3ZM0.9 17.0999V11.6999H8.1V17.0999H0.9ZM2.7 15.2999H6.3V13.4999H2.7V15.2999ZM11.7 15.2999H15.3V9.8999H11.7V15.2999ZM9.9 0.899902H17.1V6.2999H9.9V0.899902ZM11.7 2.6999V4.4999H15.3V2.6999H11.7Z"
                            fill={location.pathname === "/admin" ? "#FFFFFF" : "#525866"}
                          />
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>

                <ListItem disablePadding>
                  <BootstrapTooltip title="User Management">
                    <ListItemButton 
                      className={
                        location.pathname === "/admin/user_management" || 
                        location.pathname.startsWith("/admin/user_management/") ? "active" : ""
                      } 
                      onClick={() => { navigate("/admin/user_management") }}
                    >
                      <ListItemIcon>
                        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path 
                            d="M8.00005 11.8V13.6C6.56788 13.6 5.19437 14.1689 4.18167 15.1816C3.16898 16.1943 2.60005 17.5678 2.60005 19H0.800049C0.800049 17.0904 1.55862 15.2591 2.90888 13.9088C4.25914 12.5586 6.09049 11.8 8.00005 11.8ZM8.00005 10.9C5.01655 10.9 2.60005 8.48351 2.60005 5.50001C2.60005 2.51651 5.01655 0.100006 8.00005 0.100006C10.9835 0.100006 13.4 2.51651 13.4 5.50001C13.4 8.48351 10.9835 10.9 8.00005 10.9ZM8.00005 9.10001C9.98905 9.10001 11.6 7.48901 11.6 5.50001C11.6 3.51101 9.98905 1.90001 8.00005 1.90001C6.01105 1.90001 4.40005 3.51101 4.40005 5.50001C4.40005 7.48901 6.01105 9.10001 8.00005 9.10001ZM10.3355 16.1308C10.2214 15.6506 10.2214 15.1503 10.3355 14.6701L9.44275 14.1544L10.3427 12.5956L11.2355 13.1113C11.5939 12.7719 12.0271 12.5216 12.5 12.3805V11.35H14.3V12.3805C14.7788 12.5227 15.2108 12.7765 15.5645 13.1113L16.4573 12.5956L17.3573 14.1544L16.4645 14.6701C16.5785 15.15 16.5785 15.65 16.4645 16.1299L17.3573 16.6456L16.4573 18.2044L15.5645 17.6887C15.2062 18.0281 14.773 18.2785 14.3 18.4195V19.45H12.5V18.4195C12.0271 18.2785 11.5939 18.0281 11.2355 17.6887L10.3427 18.2044L9.44275 16.6456L10.3355 16.1308ZM13.4 16.75C13.7581 16.75 14.1015 16.6078 14.3546 16.3546C14.6078 16.1014 14.75 15.758 14.75 15.4C14.75 15.042 14.6078 14.6986 14.3546 14.4454C14.1015 14.1922 13.7581 14.05 13.4 14.05C13.042 14.05 12.6986 14.1922 12.4455 14.4454C12.1923 14.6986 12.05 15.042 12.05 15.4C12.05 15.758 12.1923 16.1014 12.4455 16.3546C12.6986 16.6078 13.042 16.75 13.4 16.75Z" 
                            fill={
                              location.pathname === "/admin/user_management" || 
                              location.pathname.startsWith("/admin/user_management/") ? "#FFFFFF" : "#525866"
                            }
                          />
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="User Management" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>

                {/* <ListItem disablePadding>
                  <BootstrapTooltip title="Plan Management">
                    <ListItemButton 
                      className={
                        location.pathname === "/admin/plan_management" || 
                        location.pathname.startsWith("/admin/plan_management/") ? "active" : ""
                      } 
                      onClick={() => { navigate("/admin/plan_management") }}
                    >
                      <ListItemIcon>
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
<path d="M10.25 2.9375C11.5962 2.9375 12.6875 1.8462 12.6875 0.5H13.8125C13.8125 1.8462 14.9038 2.9375 16.25 2.9375V4.0625C14.9038 4.0625 13.8125 5.1538 13.8125 6.5H12.6875C12.6875 5.1538 11.5962 4.0625 10.25 4.0625V2.9375ZM2 4.25C2 3.42157 2.67157 2.75 3.5 2.75H8.75V1.25H3.5C1.84314 1.25 0.5 2.59314 0.5 4.25V11.75C0.5 13.4068 1.84314 14.75 3.5 14.75H12.5C14.1568 14.75 15.5 13.4068 15.5 11.75V8H14V11.75C14 12.5785 13.3285 13.25 12.5 13.25H3.5C2.67157 13.25 2 12.5785 2 11.75V4.25Z" fill={location.pathname === "/admin/plan_management" ? "#FFFFFF" : "#525866"}/>
</svg>
                      </ListItemIcon>
                      <ListItemText primary="Plan Management" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem> */}

                <ListItem disablePadding>
                  <BootstrapTooltip title="Document">
                    <ListItemButton 
                      className={
                        location.pathname === "/admin/knowledge_base" || 
                        location.pathname.startsWith("/admin/knowledge_base/") ? "active" : ""
                      } 
                      onClick={() => { navigate("/admin/knowledge_base") }}
                    >
                      <ListItemIcon>
                        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 2V18H16V6.5L11.5 2H2ZM1 1H12L17 6V19H1V1ZM10 4V7H13V4H10ZM4 6H8V7H4V6ZM4 10H14V11H4V10ZM4 14H14V15H4V14Z" fill={location.pathname === "/admin/knowledge_base" ? "#FFFFFF" : "#525866"} fillRule="evenodd" clipRule="evenodd"/>
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Document" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>
              </>
            ) : (
              // Regular user menu items
              <>
                <ListItem disablePadding>
                  <BootstrapTooltip title="Chat">
                    <ListItemButton className={location.pathname === "/dashboard" ? "active" : ""} onClick={() => { navigate("/dashboard") }}>
                      <ListItemIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M10.2 3.8999H13.8C15.7096 3.8999 17.5409 4.65847 18.8912 6.00873C20.2414 7.359 21 9.19034 21 11.0999C21 13.0095 20.2414 14.8408 18.8912 16.1911C17.5409 17.5413 15.7096 18.2999 13.8 18.2999V21.4499C9.3 19.6499 3 16.9499 3 11.0999C3 9.19034 3.75857 7.359 5.10883 6.00873C6.45909 4.65847 8.29044 3.8999 10.2 3.8999ZM12 16.4999H13.8C14.5091 16.4999 15.2113 16.3602 15.8665 16.0889C16.5216 15.8175 17.1169 15.4197 17.6184 14.9183C18.1198 14.4168 18.5176 13.8216 18.7889 13.1664C19.0603 12.5112 19.2 11.809 19.2 11.0999C19.2 10.3908 19.0603 9.68857 18.7889 9.03341C18.5176 8.37825 18.1198 7.78296 17.6184 7.28153C17.1169 6.78009 16.5216 6.38233 15.8665 6.11095C15.2113 5.83958 14.5091 5.6999 13.8 5.6999H10.2C8.76783 5.6999 7.39432 6.26883 6.38162 7.28153C5.36893 8.29422 4.8 9.66773 4.8 11.0999C4.8 14.3489 7.0158 16.4693 12 18.7319V16.4999Z"
                            fill={location.pathname === "/dashboard" ? "#FFFFFF" : "#525866"}
                          />
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Chat" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>
                <ListItem disablePadding>
                  <BootstrapTooltip title="Dashboard">
                    <ListItemButton className={location.pathname === "/dashboard/test_score" ? "active" : ""} onClick={() => {
                      if (user.remaining_tokens <= 0) {
                        navigate('/price')
                      } else  {
                        navigate('/dashboard/test_score')
                      }
                      // else if (user.is_paid == true && user.test_given == false) {
                      //   setShowDialog(true);
                      // }
                    }}>
                      <ListItemIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <path
                            d="M9.9 17.0999V8.0999H17.1V17.0999H9.9ZM0.9 9.8999V0.899902H8.1V9.8999H0.9ZM6.3 8.0999V2.6999H2.7V8.0999H6.3ZM0.9 17.0999V11.6999H8.1V17.0999H0.9ZM2.7 15.2999H6.3V13.4999H2.7V15.2999ZM11.7 15.2999H15.3V9.8999H11.7V15.2999ZM9.9 0.899902H17.1V6.2999H9.9V0.899902ZM11.7 2.6999V4.4999H15.3V2.6999H11.7Z"
                            fill={location.pathname === "/dashboard/test_score" ? "#FFFFFF" : "#525866"}
                          />
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>
                <ListItem disablePadding>
                  <BootstrapTooltip title="Learning">
                    <ListItemButton className={location.pathname === "/dashboard/course_list" || location.pathname === "/dashboard/enrolled_course" ? "active" : ""} onClick={() => {
                      if (user.remaining_tokens <= 0) {
                      navigate('/price')
                      } else{
                        navigate('/dashboard/course_list')
                      }
                    }}>
                      <ListItemIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12.9 20.0999V21.8999H11.1V20.0999H3.9C3.66131 20.0999 3.43239 20.0051 3.2636 19.8363C3.09482 19.6675 3 19.4386 3 19.1999V4.7999C3 4.56121 3.09482 4.33229 3.2636 4.16351C3.43239 3.99473 3.66131 3.8999 3.9 3.8999H9.3C9.81083 3.8993 10.3159 4.00769 10.7815 4.21783C11.2471 4.42797 11.6625 4.73503 12 5.11851C12.3375 4.73503 12.7529 4.42797 13.2185 4.21783C13.6841 4.00769 14.1892 3.8993 14.7 3.8999H20.1C20.3387 3.8999 20.5676 3.99473 20.7364 4.16351C20.9052 4.33229 21 4.56121 21 4.7999V19.1999C21 19.4386 20.9052 19.6675 20.7364 19.8363C20.5676 20.0051 20.3387 20.0999 20.1 20.0999H12.9ZM19.2 18.2999V5.69991H14.7C14.2226 5.69991 13.7648 5.88955 13.4272 6.22711C13.0896 6.56468 12.9 7.02252 12.9 7.49991V18.2999H19.2ZM11.1 18.2999V7.49991C11.1 7.02252 10.9104 6.56468 10.5728 6.22711C10.2352 5.88955 9.77739 5.69991 9.3 5.69991H4.8V18.2999H11.1Z"
                            fill={
                              location.pathname === "/dashboard/course_list" || 
                              location.pathname === "/dashboard/enrolled_course" ? "#FFFFFF" : "#525866"
                            }
                          />
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Learning" />
                    </ListItemButton>
                  </BootstrapTooltip>
                </ListItem>               
              </>
            )}
          </List>
        </div>
      </div>
      {/* {showDialog && (
        <PersonalizeLearningDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
        />
      )} */}
    </>
  );
};

export default Sidebar;